import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Accordion, Alert, Button, Table } from 'react-bootstrap';
import { getRetests, undoRetestRiskAccept } from '../../Services/PtaasApiService';
import { Retest, RetestFinding } from '../../Models/Retest';
import { ShieldIcon } from '../Global/Icons';
import RatingProps from '../../Utilities/RatingProps';

// Mirrors the server guard in RetestService.UndoRiskAcceptAsync. Undo on a Complete or
// Cancelled retest is rejected there because it would rewrite trajectory history that has
// already been snapshotted, so we must not offer the action here either. Quote Available is
// permitted: the quote may be published but the risk-accept is still reversible until acceptance.
const UNDOABLE_STATUSES = ['Quote Requested', 'Quote Available', 'In Progress'];

interface Props {
	projectId: string;
	onUndone?: () => void;
}

interface AcceptedRow {
	retest: Retest;
	finding: RetestFinding;
}

export default function RiskAcceptedFindings({ projectId, onUndone }: Props) {
	const { instance, accounts } = useMsal();
	const [rows, setRows] = useState<AcceptedRow[]>([]);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (accounts.length === 0) return;
		try {
			const retests = await getRetests(instance, accounts[0], projectId);
			setRows(
				retests.flatMap((retest) =>
					retest.findings
						.filter((finding) => finding.disposition === 'RiskAccepted')
						.map((finding) => ({ retest, finding }))
				)
			);
			setError(null);
		} catch (e) {
			// getRetests (Cosmos RetestRequest) and getEligibleRetestFindings (Canopy, used by
			// the pool table above) are two independent endpoints against two different backing
			// stores: one failing tells us nothing about the other, so there is no
			// double-reporting to avoid here. Concretely: if the Cosmos RetestRequest container
			// hasn't been created in an environment, getRetests 500s while
			// getEligibleRetestFindings still succeeds and returns [] -- the page would render
			// "no findings awaiting retest" as if all-clear, while a mis-accepted finding sits
			// invisible and unrecoverable. Surface it instead of going quiet.
			setRows([]);
			setError((e as Error).message || 'Risk accepted findings could not be loaded.');
		}
	}, [projectId, instance, accounts]);

	useEffect(() => {
		load();
	}, [load]);

	const handleUndo = async (row: AcceptedRow) => {
		setError(null);
		try {
			await undoRetestRiskAccept(instance, accounts[0], row.retest.id, row.finding.findingUuid);
			await load();
			onUndone?.();
		} catch (e) {
			// undoRetestRiskAccept throws `new Error(await response.text())`, so an empty
			// body yields an empty message. Falling back keeps the alert visible.
			setError((e as Error).message || 'The risk accept could not be undone.');
		}
	};

	// Nothing accepted and no load failure means nothing to recover, so the section itself is
	// noise. A load failure is not "nothing accepted" -- it's "we could not tell" -- and must
	// not collapse into that same silence (see the load() catch above), so it gets its own
	// alert even though there are no rows to show alongside it.
	if (rows.length === 0) {
		if (!error) return null;
		return <Alert variant='danger' className='mt-4'>{error}</Alert>;
	}

	return (
		<Accordion className='mt-4'>
			<Accordion.Item eventKey='0'>
				<Accordion.Header>Risk accepted ({rows.length})</Accordion.Header>
				<Accordion.Body>
					{error && <Alert variant='danger'>{error}</Alert>}
					<Table hover size='sm' className='mb-0'>
						<thead>
							<tr>
								<th>Severity</th>
								<th>Finding</th>
								<th>Reason</th>
								<th>Retest</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => {
								const { name, color } =
									RatingProps[row.finding.severityAtRequest as keyof typeof RatingProps] ?? RatingProps[0];
								return (
									<tr key={`${row.retest.id}-${row.finding.findingUuid}`}>
										<td><ShieldIcon className={'me-2 ' + color} />{name}</td>
										<td>{row.finding.title}</td>
										<td>{row.finding.reason}</td>
										<td>Retest {row.retest.retestNumber}</td>
										<td className='text-end'>
											{UNDOABLE_STATUSES.includes(row.retest.status) && (
												<Button size='sm' variant='outline-secondary' onClick={() => handleUndo(row)}>
													Undo
												</Button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				</Accordion.Body>
			</Accordion.Item>
		</Accordion>
	);
}
